import { UserFormData } from "@repo/types";
import { CheckIcon, UsersIcon } from "lucide-react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Badge } from "../../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { useGroupe } from "../../../../hooks/api/groupe";
import { useProfil } from "../../../../hooks/api/profil";
import { GroupSelector } from "../group-selector";
import { ProfileSelector } from "../profile-selector";

type FormValues = UserFormData & { confirmPassword?: string };

interface UserAssignmentSectionProps {
  form: UseFormReturn<FormValues>;
  fieldErrors: Record<string, string>;
}

export function UserAssignmentSection({
  form,
  fieldErrors,
}: UserAssignmentSectionProps) {
  const profilId = useWatch({ control: form.control, name: "profil_id" });
  const groupeId = useWatch({ control: form.control, name: "groupe_id" });
  const { data: profilData } = useProfil(profilId || 0);
  const { data: groupeData } = useGroupe(groupeId || 0);

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UsersIcon className="h-5 w-5 text-primary" />
          Affectation et rôles
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h4 className="text-sm font-medium mb-4">Profil utilisateur</h4>
            {profilId && profilData?.data && (
              <div className="mb-3 p-3 bg-muted/40 border border-border rounded-md flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckIcon className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">
                    {profilData.data.name || "Sans nom"}
                  </span>
                </div>
                <Badge variant="secondary">Sélectionné</Badge>
              </div>
            )}
            <ProfileSelector
              control={form.control}
              name="profil_id"
              fieldError={fieldErrors.profil_id}
            />
          </div>

          <div>
            <h4 className="text-sm font-medium mb-4">Groupe d'appartenance</h4>
            {groupeId && groupeData?.data && (
              <div className="mb-3 p-3 bg-muted/40 border border-border rounded-md">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <CheckIcon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      {groupeData.data.name || "Sans nom"}
                    </span>
                  </div>
                  <Badge variant="secondary">Sélectionné</Badge>
                </div>
                {groupeData.data.name_societe && (
                  <p className="text-xs text-muted-foreground mt-1 ml-6">
                    {groupeData.data.name_societe}
                  </p>
                )}
              </div>
            )}
            <GroupSelector
              control={form.control}
              name="groupe_id"
              fieldError={fieldErrors.groupe_id}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
